import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SettingsIcon from '@mui/icons-material/Settings';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import LiveClockPanel from '@/components/LiveClockPanel';
import { liveMatchHeadline, liveSessionMetaLine } from '@/utils/liveSessionDisplay';
import LiveSessionFormDialog, { type LiveSessionFormValues } from '@/components/LiveSessionFormDialog';
import { EventFormDialog } from '@/components/SessionFormDialog';
import { getPeriodCapSeconds, isClockRunning } from '@/utils/liveClock';
import { type LiveActionDefinition } from '@/components/live/LiveActionZones';
import LiveEventPickDialog from '@/components/live/LiveEventPickDialog';
import LiveEventsCard from '@/components/live/LiveEventsCard';
import LiveJudgementBar, { judgementToApiValue, type LiveJudgementChoice } from '@/components/live/LiveJudgementBar';
import LiveRecordingMode from '@/components/live/LiveRecordingMode';
import { usePageDocumentTitle } from '@/contexts/PageDocumentTitleContext';
import type { LiveSession, SessionEvent } from '@/types';

function computeCurrentSeconds(session: LiveSession): number {
  const accumulated = session.accumulated_seconds || 0;
  if (isClockRunning(session) && session.clock_started_at) {
    const started = new Date(session.clock_started_at).getTime();
    return accumulated + Math.max(0, Math.floor((Date.now() - started) / 1000));
  }
  return accumulated;
}

type MobileTab = 'capture' | 'timeline';

export default function LiveDetailPage() {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [session, setSession] = useState<LiveSession | null>(null);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [recordingMode, setRecordingMode] = useState(false);
  const [editEvent, setEditEvent] = useState<SessionEvent | null>(null);
  const [mobileTab, setMobileTab] = useState<MobileTab>('capture');
  const [pendingJudgement, setPendingJudgement] = useState<LiveJudgementChoice | null>(null);
  const [pendingMatchSecond, setPendingMatchSecond] = useState<number | null>(null);
  const [pendingPeriod, setPendingPeriod] = useState<string | null>(null);
  const [eventPickOpen, setEventPickOpen] = useState(false);
  const [submittingEvents, setSubmittingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoCapRef = useRef<string | null>(null);

  usePageDocumentTitle(session ? liveMatchHeadline(session) : null);

  const load = useCallback(async () => {
    if (!id) return;
    const res = await api.get<{ session: LiveSession; events: SessionEvent[] }>(`/live-sessions/${id}`);
    setSession(res.data.session);
    setEvents(res.data.events);
    setCurrentSeconds(res.data.session.current_match_second ?? computeCurrentSeconds(res.data.session));
  }, [id]);

  useEffect(() => {
    load().catch(() => setError('無法載入場次'));
  }, [load]);

  useEffect(() => {
    if (!session || !isClockRunning(session)) return;
    const interval = window.setInterval(() => {
      setCurrentSeconds(computeCurrentSeconds(session));
    }, 1000);
    return () => clearInterval(interval);
  }, [session]);

  const handleClockAction = useCallback(
    async (action: string, extra?: { adjust_seconds?: number; period?: string; seconds?: number }) => {
      if (!id) return;
      const res = await api.post<{ session: LiveSession }>(`/live-sessions/${id}/clock`, { action, ...extra });
      setSession(res.data.session);
      setCurrentSeconds(res.data.session.current_match_second ?? computeCurrentSeconds(res.data.session));
    },
    [id]
  );

  useEffect(() => {
    if (!session?.can_edit || !isClockRunning(session)) {
      autoCapRef.current = null;
      return;
    }
    const cap = getPeriodCapSeconds(session.current_period, session.match_duration_minutes ?? 30);
    if (cap == null || currentSeconds < cap) {
      autoCapRef.current = null;
      return;
    }
    const key = `${session.id}-${session.current_period}-${cap}`;
    if (autoCapRef.current === key) return;
    autoCapRef.current = key;
    if (session.current_period === '1H') {
      void handleClockAction('halftime');
    } else {
      void handleClockAction('pause');
    }
  }, [currentSeconds, session, handleClockAction]);

  const openEventPick = (judgement: LiveJudgementChoice) => {
    setPendingJudgement(judgement);
    setPendingMatchSecond(currentSeconds);
    setPendingPeriod(session?.current_period ?? null);
    setEventPickOpen(true);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(18);
    }
  };

  const closeEventPick = () => {
    setEventPickOpen(false);
    setPendingJudgement(null);
    setPendingMatchSecond(null);
    setPendingPeriod(null);
  };

  const handleActionsSubmit = async (actions: LiveActionDefinition[]) => {
    if (
      !id ||
      !session?.can_edit ||
      !pendingJudgement ||
      pendingMatchSecond == null ||
      !pendingPeriod ||
      actions.length === 0
    ) {
      return;
    }
    setSubmittingEvents(true);
    try {
      await Promise.all(
        actions.map((action) =>
          api.post('/events', {
            session_id: id,
            session_type: 'live',
            event_type: action.key,
            note: null,
            team_side: null,
            match_second: pendingMatchSecond,
            period: pendingPeriod,
            judgement: judgementToApiValue(pendingJudgement),
            on_field_decision: action.zone === 'discipline' ? null : action.key,
            on_field_punishment: action.zone === 'discipline' ? action.key : null,
          })
        )
      );
      closeEventPick();
      await load();
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate([12, 24, 12]);
      }
    } finally {
      setSubmittingEvents(false);
    }
  };

  const handleUpdateSession = async (values: LiveSessionFormValues) => {
    if (!id) return;
    await api.put(`/live-sessions/${id}`, values);
    setSettingsOpen(false);
    await load();
  };

  const handleEditEvent = async (values: { event_type: string; note: string; team_side?: string | null }) => {
    if (!editEvent) return;
    await api.put(`/events/${editEvent.id}`, {
      event_type: values.event_type,
      note: values.note,
      team_side: null,
    });
    setEditEvent(null);
    await load();
  };

  const handleDeleteEvent = async (event: SessionEvent) => {
    if (!confirm('刪除此事件？')) return;
    await api.delete(`/events/${event.id}`);
    await load();
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!session) return <Typography>載入中…</Typography>;

  const isEnded = session.status === 'ended';
  const canCapture = !!session.can_edit && !isEnded;

  const eventsCard = (
    <LiveEventsCard
      events={events}
      canEdit={session.can_edit && !isEnded}
      onEdit={setEditEvent}
      onDelete={handleDeleteEvent}
    />
  );

  const zonesCard = (
    <Card variant="outlined">
      <CardContent sx={{ pb: '16px !important' }}>
        {canCapture ? (
          <LiveJudgementBar onSelect={openEventPick} />
        ) : (
          <Alert severity="info">
            比賽已結束或你沒有編輯權限，事件區已鎖定。
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <PageHeader
        backTo="/live"
        backLabel="即時記錄"
        icon={<SportsHandballIcon />}
        title={liveMatchHeadline(session)}
        titleVariant="h5"
        subtitle={liveSessionMetaLine(session)}
        chips={
          <Chip size="small" color="secondary" variant="outlined" label={`${events.length} 個標記`} />
        }
        actions={
          <>
            {canCapture && (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<FullscreenIcon />}
                onClick={() => setRecordingMode(true)}
              >
                全屏記錄
              </Button>
            )}
            <Button size="small" startIcon={<DownloadIcon />} href={`/api/live-sessions/${id}/export?format=json`} target="_blank">
              JSON
            </Button>
            <Button size="small" startIcon={<DownloadIcon />} href={`/api/live-sessions/${id}/export?format=xls`} target="_blank">
              Excel
            </Button>
            {session.can_edit && (
              <Button size="small" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}>
                設定
              </Button>
            )}
          </>
        }
        mb={1.5}
      />

      {isMobile ? (
        <>
          <Card
            variant="outlined"
            sx={{
              position: 'sticky',
              top: 8,
              zIndex: 5,
              mb: 1.2,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
              <LiveClockPanel
                session={session}
                currentSeconds={currentSeconds}
                onClockAction={handleClockAction}
                disabled={!session.can_edit}
                compact
              />
            </CardContent>
          </Card>

          <Tabs value={mobileTab} onChange={(_, v) => setMobileTab(v)} sx={{ mb: 1 }}>
            <Tab value="capture" label={`記錄 (${events.length})`} />
            <Tab value="timeline" label="事件列表" />
          </Tabs>

          {mobileTab === 'capture' ? zonesCard : eventsCard}
        </>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <LiveClockPanel
                  session={session}
                  currentSeconds={currentSeconds}
                  onClockAction={handleClockAction}
                  disabled={!session.can_edit}
                />
              </CardContent>
            </Card>
            {zonesCard}
          </Grid>
          <Grid item xs={12} md={6}>
            {eventsCard}
          </Grid>
        </Grid>
      )}

      {recordingMode && (
        <LiveRecordingMode
          session={session}
          currentSeconds={currentSeconds}
          eventCount={events.length}
          events={events}
          canCapture={canCapture}
          canEditEvents={session.can_edit && !isEnded}
          onJudgementSelect={openEventPick}
          onClockAction={handleClockAction}
          onEditEvent={setEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onClose={() => setRecordingMode(false)}
        />
      )}

      <LiveEventPickDialog
        open={eventPickOpen}
        judgement={pendingJudgement}
        snapshotMatchSecond={pendingMatchSecond ?? 0}
        liveMatchSecond={currentSeconds}
        snapshotPeriod={pendingPeriod}
        livePeriod={session.current_period}
        clockRunning={isClockRunning(session)}
        onClose={closeEventPick}
        onSubmit={(actions) => void handleActionsSubmit(actions)}
        submitting={submittingEvents}
      />

      <LiveSessionFormDialog
        open={settingsOpen}
        title="編輯場次設定"
        initial={{
          competition_name: session.competition_name || '',
          home_team: session.home_team || '',
          away_team: session.away_team || '',
          match_date: session.match_date || '',
          notes: session.notes || '',
          match_duration_minutes: session.match_duration_minutes ?? 30,
        }}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleUpdateSession}
      />

      <EventFormDialog
        open={!!editEvent}
        variant="live"
        initial={editEvent || undefined}
        onClose={() => setEditEvent(null)}
        onSubmit={(v) => handleEditEvent(v as { event_type: string; note: string; team_side?: string | null })}
        zIndex={recordingMode ? theme.zIndex.modal + 25 : undefined}
      />
    </Box>
  );
}
